import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Filter, 
  Package,
  Euro,
  Star,
  AlertCircle,
  CheckCircle,
  X,
  Save,
  Image as ImageIcon,
  Tag,
  Clock,
  TrendingUp,
  Scissors,
  Wrench,
  Leaf,
  Award,
  Info,
  Settings,
  ChevronDown,
  ChevronUp,
  Move,
  XCircle,
  Ban,
  Power,
  PowerOff
} from 'lucide-react';
import apiService from '../services/api';
import CloudinaryUpload from './CloudinaryUpload';
import ProduitModal from './ProduitModal';
import ProduitAssistantWizard from './produit/ProduitAssistantWizard';
import OffreCreatePicker from './offre/OffreCreatePicker';
import { getArchetypeById } from '../config/produitArchetypes';
import Toast from './Toast';
import { useToast } from '../hooks/useToast';
import {
  OFFRE_DESTINATION,
  destinationToTypeProduit,
  describeOffreCreateTargets,
} from '../utils/offreCreateRouting';
import { consumeConvertedPrestationFromSave } from '../utils/postConversionPrestationNav';
import {
  resolveProduitOptionsCount,
  formatProduitOptionsBadgeLabel,
} from '../utils/produitOptionsCount';
import { produitsAfterModalSave } from '../utils/produitsAfterModalSave';
import { matchSearchText } from '../utils/normalizeSearchText';

const ALLERGENES_OPTIONS = [
  'gluten', 'crustaces', 'oeufs', 'poissons', 'arachides', 
  'soja', 'lait', 'fruits_a_coque', 'celeri', 'moutarde', 
  'sesame', 'sulfites', 'lupin', 'mollusques'
];

const ProduitsManager = forwardRef(({
  commercantId,
  onAddProduit,
  embeddedAddMode = false,
  /** Pont O1 → Mes Prestations (création Prestation réservable, sans twin SERVICE). */
  onRequestPrestationReservable,
  /** Édition d’une Prestation depuis le catalogue unifié. */
  onRequestEditPrestation,
}, ref) => {
  const { toasts, toast, removeToast } = useToast();
  const [commercantTempsPreparation, setCommercantTempsPreparation] = useState(15);
  const [produits, setProduits] = useState([]);
  const [prestationsCatalogue, setPrestationsCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('ordre');
  const [showModal, setShowModal] = useState(false);
  /** 'PHYSIQUE' | 'SERVICE' — type catalogue à la création (≠ Prestation RDV) */
  const [createTypeProduit, setCreateTypeProduit] = useState('PHYSIQUE');
  /** Shell Offre O1 (remplace le picker Produit/Service). */
  const [showOffreCreatePicker, setShowOffreCreatePicker] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  /** Archétype + réponses assistant pour la création en cours (étape 4 → ProduitModal). */
  const createArchetypeRef = useRef(null);
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [showOptionsSection, setShowOptionsSection] = useState(false);
  const [saving, setSaving] = useState(false); // Protection contre double-clic
  const [categories, setCategories] = useState([]); // Catégories existantes pour ce commerçant
  const [availabilityMenuOpen, setAvailabilityMenuOpen] = useState(null); // ID du produit pour lequel le menu est ouvert
  const availabilityMenuRefs = useRef({}); // Une ref par produit
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    prix: '',
    prixPromo: '',
    categorie: '',
    images: [''],
    actif: true,
    stock: -1,
    favori: false,
    ordre: 0,
    tempsPreparation: 15,
    vegetarien: false,
    vegan: false,
    sansGluten: false,
    allergenes: [],
    ingredients: [],
    options: [] // NOUVEAU : Options du produit
  });

  useEffect(() => {
    fetchProduits();
  }, [commercantId]);

  useEffect(() => {
    if (!commercantId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCommercant(commercantId);
        const c = data?.commercant || data;
        const prep = Number(c?.tempsPreparation);
        if (!cancelled && Number.isFinite(prep) && prep >= 0) {
          setCommercantTempsPreparation(prep);
        }
      } catch {
        /* keep 15 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commercantId]);

  const fetchProduits = async () => {
    try {
      setLoading(true);
      // En admin, on récupère TOUS les produits (actifs et inactifs) pour pouvoir gérer leur état
      // PR4 PAYLOAD: Le backend limite à 50 par page, donc on fait plusieurs appels si nécessaire
      let allProduits = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 50; // Maximum autorisé par le backend
      
      if (!commercantId) {
        setError('Commerçant non trouvé');
        setLoading(false);
        return null;
      }
      
      while (hasMore) {
        const response = await apiService.getProduitsByCommercant(commercantId, true, { page, pageSize });
        const produitsPage = response?.produits || [];
        allProduits = [...allProduits, ...produitsPage];
        
        // Vérifier s'il y a encore des produits à récupérer
        const total = response?.total || 0;
        hasMore = allProduits.length < total && produitsPage.length === pageSize;
        page++;
      }
      
      setProduits(allProduits);

      let prestList = [];
      try {
        const prestResponse = await apiService.getPrestationsByCommercant(commercantId);
        const list = Array.isArray(prestResponse?.prestations)
          ? prestResponse.prestations
          : Array.isArray(prestResponse)
            ? prestResponse
            : [];
        prestList = list.filter(
          (p) =>
            p?.actif !== false &&
            typeof p?.categorie === 'string' &&
            p.categorie.trim().length > 0
        );
      } catch (err) {
        console.warn('[ProduitsManager] Prestations catalogue:', err?.message || err);
      }
      setPrestationsCatalogue(prestList);

      // Extraire les catégories distinctes (filtrées et triées)
      const cats = Array.from(
        new Set(
          [
            ...(allProduits || []).map((p) => p.categorie),
            ...prestList.map((p) => p.categorie),
          ].filter((c) => typeof c === 'string' && c.trim().length > 0)
        )
      ).sort();
      setCategories(cats);
      setError('');
      return allProduits;
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setError('Erreur lors du chargement des produits');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openCreateFlow = () => {
    createArchetypeRef.current = null;
    setShowOffreCreatePicker(true);
  };

  const startCreateWithType = (typeProduit) => {
    setCreateTypeProduit(typeProduit === 'SERVICE' ? 'SERVICE' : 'PHYSIQUE');
    setShowOffreCreatePicker(false);
    handleAddProduit();
  };

  const handleOffreCreateConfirm = (destination) => {
    const targets = describeOffreCreateTargets(destination);
    if (!targets.ok || (targets.createsProduit && targets.createsPrestation)) {
      toast.error('Impossible de créer cette offre.');
      return;
    }

    // Prestation (réservable ou non) → même moteur Prestation, flag reservationEnLigne.
    if (
      destination === OFFRE_DESTINATION.PRESTATION_RESERVABLE ||
      destination === OFFRE_DESTINATION.PRESTATION_SANS_RESERVATION
    ) {
      setShowOffreCreatePicker(false);
      if (typeof onRequestPrestationReservable === 'function') {
        onRequestPrestationReservable({
          reservationEnLigne:
            destination === OFFRE_DESTINATION.PRESTATION_RESERVABLE,
        });
      } else {
        toast.info(
          'Ouvrez l’onglet Mes Prestations pour créer une prestation.'
        );
      }
      return;
    }

    const typeProduit = destinationToTypeProduit(destination);
    if (!typeProduit) {
      toast.error('Destination d’offre inconnue.');
      return;
    }
    startCreateWithType(typeProduit);
  };

  const handleAssistantComplete = ({ archetypeId, answers, sourceProductId, mode }) => {
    setShowAssistant(false);
    createArchetypeRef.current = { archetypeId, answers: answers ?? {}, sourceProductId, mode };
    const label = getArchetypeById(archetypeId)?.label ?? archetypeId;
    toast.info(`Modèle « ${label} » — fiche produit guidée.`);
    setCreateTypeProduit('PHYSIQUE');
    handleAddProduit();
  };

  const handleRequestChangeArchetype = () => {
    setShowModal(false);
    setSelectedProduit(null);
    createArchetypeRef.current = null;
    setShowAssistant(true);
  };

  const handleAddProduit = () => {
    setFormData({
      nom: '',
      description: '',
      prix: '',
      prixPromo: '',
      categorie: '',
      images: [''],
      actif: true,
      stock: -1,
      favori: false,
      ordre: 0,
      tempsPreparation: 15,
      vegetarien: false,
      vegan: false,
      sansGluten: false,
      allergenes: [],
      ingredients: [],
      options: []
    });
    setSelectedProduit(null);
    setShowOptionsSection(false);
    setShowModal(true);
  };

  const handleEditProduit = async (produit) => {
    if (produit?._offerKind === 'prestation') {
      if (typeof onRequestEditPrestation === 'function') {
        onRequestEditPrestation(produit.id);
      } else {
        toast.error('Impossible d’ouvrir l’édition de la prestation.');
      }
      return;
    }
    // Toujours charger le produit individuellement pour avoir toutes les options/variantes
    try {
      const response = await apiService.getProduit(produit.id);
      const produitComplet = response?.produit || response || produit;
      
      setFormData({
        nom: produitComplet.nom || '',
        description: produitComplet.description || '',
        prix: produitComplet.prix || '',
        prixPromo: produitComplet.prixPromo || '',
        categorie: produitComplet.categorie || '',
        images: produitComplet.images && produitComplet.images.length > 0 ? produitComplet.images : [''],
        actif: produitComplet.actif !== false,
        stock: produitComplet.stock !== undefined ? produitComplet.stock : -1,
        favori: produitComplet.favori || false,
        ordre: produitComplet.ordre || 0,
        tempsPreparation: produitComplet.tempsPreparation || 15,
        vegetarien: produitComplet.vegetarien || false,
        vegan: produitComplet.vegan || false,
        sansGluten: produitComplet.sansGluten || false,
        allergenes: produitComplet.allergenes || [],
        ingredients: produitComplet.ingredients || [],
        options: produitComplet.options || []
      });
      setSelectedProduit(produitComplet);
      setShowOptionsSection(produitComplet.options && produitComplet.options.length > 0);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      // En cas d'erreur, utiliser les données de la liste (comme avant)
      setFormData({
        nom: produit.nom || '',
        description: produit.description || '',
        prix: produit.prix || '',
        prixPromo: produit.prixPromo || '',
        categorie: produit.categorie || '',
        images: produit.images && produit.images.length > 0 ? produit.images : [''],
        actif: produit.actif !== false,
        stock: produit.stock !== undefined ? produit.stock : -1,
        favori: produit.favori || false,
        ordre: produit.ordre || 0,
        tempsPreparation: produit.tempsPreparation || 15,
        vegetarien: produit.vegetarien || false,
        vegan: produit.vegan || false,
        sansGluten: produit.sansGluten || false,
        allergenes: produit.allergenes || [],
        ingredients: produit.ingredients || [],
        options: produit.options || []
      });
      setSelectedProduit(produit);
      setShowOptionsSection(produit.options && produit.options.length > 0);
      setShowModal(true);
    }
  };

  const handleSaveProduit = async () => {
    if (saving) return; // Éviter les double-clics
    
    try {
      if (!formData.nom || !formData.prix) {
        alert('Le nom et le prix sont obligatoires');
        return;
      }

      setSaving(true);

      const produitData = {
        nom: formData.nom,
        description: formData.description,
        prix: parseFloat(formData.prix) || 0,
        prixPromo: formData.prixPromo ? parseFloat(formData.prixPromo) : null,
        categorie: formData.categorie,
        images: formData.images.filter(img => img && img.trim() !== ''),
        actif: formData.actif,
        stock: parseInt(formData.stock) || -1,
        favori: formData.favori,
        ordre: parseInt(formData.ordre) || 0,
        tempsPreparation: parseInt(formData.tempsPreparation) || 15,
        vegetarien: formData.vegetarien,
        vegan: formData.vegan,
        sansGluten: formData.sansGluten,
        allergenes: formData.allergenes,
        ingredients: formData.ingredients,
        commercantId,
        options: formData.options // Inclure les options
      };

      if (selectedProduit) {
        await apiService.updateProduit(selectedProduit.id, produitData);
      } else {
        await apiService.createProduit(produitData);
      }

      await fetchProduits();
      setShowModal(false);
      setSelectedProduit(null);
      toast.success(selectedProduit ? 'Produit modifié avec succès' : 'Produit ajouté avec succès');
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      toast.error(err.message || 'Erreur lors de la sauvegarde du produit');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduit = async (produitId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await apiService.deleteProduit(produitId);
        await fetchProduits();
        toast.success('Produit supprimé avec succès');
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        toast.error('Erreur lors de la suppression du produit');
      }
    }
  };

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideAnyMenu = Object.values(availabilityMenuRefs.current).some(ref => 
        ref && ref.current && ref.current.contains(event.target)
      );
      if (!isClickInsideAnyMenu && availabilityMenuOpen) {
        setAvailabilityMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [availabilityMenuOpen]);

  const handleSetIndisponibleVisible = async (produit) => {
    try {
      await apiService.updateProduit(produit.id, { 
        actif: true, 
        indisponible: true 
      });
      await fetchProduits();
      setAvailabilityMenuOpen(null);
      toast.warning('Produit marqué comme indisponible (reste visible sur le site)');
    } catch (err) {
      console.error('❌ Erreur lors de la mise en indisponible:', err);
      toast.error(err.message || 'Erreur lors de la mise en indisponible');
    }
  };

  const handleSetIndisponibleMasque = async (produit) => {
    try {
      await apiService.updateProduit(produit.id, { 
        actif: false,
        indisponible: false 
      });
      await fetchProduits();
      setAvailabilityMenuOpen(null);
      toast.info(
        'Produit retiré du site. Visible dans l\'admin (filtre « Tous ») et réactivable à tout moment.',
        { duration: 7000 }
      );
    } catch (err) {
      console.error('❌ Erreur lors de la mise hors ligne:', err);
      toast.error(err.message || 'Erreur lors de la mise hors ligne');
    }
  };

  const handleSetDisponible = async (produit) => {
    try {
      await apiService.updateProduit(produit.id, { 
        actif: true,
        indisponible: false 
      });
      await fetchProduits();
      setAvailabilityMenuOpen(null);
      toast.success('Produit rendu disponible');
    } catch (err) {
      console.error('❌ Erreur lors de la mise en disponible:', err);
      toast.error(err.message || 'Erreur lors de la mise en disponible');
    }
  };

  const handleToggleFavori = async (produit) => {
    try {
      await apiService.updateProduit(produit.id, { favori: !produit.favori });
      await fetchProduits();
    } catch (err) {
      console.error('Erreur lors du changement de favori:', err);
    }
  };

  // Gestion des images
  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, '']
    });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: newImages.length > 0 ? newImages : ['']
    });
  };

  const updateImageField = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({
      ...formData,
      images: newImages
    });
  };

  // Gestion des allergènes
  const toggleAllergene = (allergene) => {
    const newAllergenes = formData.allergenes.includes(allergene)
      ? formData.allergenes.filter(a => a !== allergene)
      : [...formData.allergenes, allergene];
    setFormData({ ...formData, allergenes: newAllergenes });
  };

  // ============================================
  // GESTION DES OPTIONS (NOUVEAU)
  // ============================================

  const addOption = () => {
    const newOption = {
      nom: '',
      type: 'CHOIX_UNIQUE',
      obligatoire: false,
      choixMinimum: 1,
      choixMaximum: 1,
      ordre: formData.options.length,
      choix: [{ nom: '', prix: 0, ordre: 0 }]
    };
    setFormData({
      ...formData,
      options: [...formData.options, newOption]
    });
    setShowOptionsSection(true);
  };

  const removeOption = (optionIndex) => {
    const newOptions = formData.options.filter((_, i) => i !== optionIndex);
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const updateOption = (optionIndex, field, value) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      [field]: value
    };
    if (field === 'type') {
      newOptions[optionIndex].choixMultiple = value === 'CHOIX_MULTIPLE';
      if (value === 'CHOIX_UNIQUE') {
        newOptions[optionIndex].choixMinimum = 1;
        newOptions[optionIndex].choixMaximum = 1;
      } else if (value === 'CHOIX_MULTIPLE') {
        newOptions[optionIndex].choixMinimum = 0;
        newOptions[optionIndex].choixMaximum = 10;
      }
    } else if (field === 'choixMultiple') {
      newOptions[optionIndex].type = value ? 'CHOIX_MULTIPLE' : 'CHOIX_UNIQUE';
    }
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const addChoixToOption = (optionIndex) => {
    const newOptions = [...formData.options];
    const newChoix = { 
      nom: '', 
      prix: 0, 
      ordre: newOptions[optionIndex].choix.length 
    };
    newOptions[optionIndex].choix.push(newChoix);
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const removeChoixFromOption = (optionIndex, choixIndex) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].choix = newOptions[optionIndex].choix.filter((_, i) => i !== choixIndex);
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const updateChoix = (optionIndex, choixIndex, field, value) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].choix[choixIndex] = {
      ...newOptions[optionIndex].choix[choixIndex],
      [field]: value
    };
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchProduits,
    openCreateProduit: () => {
      openCreateFlow();
    },
    openEditProduit: async (produitId) => {
      // Chercher d'abord dans la liste locale
      let produit = produits.find(p => p.id === produitId);
      
      // Si pas trouvé localement, charger depuis l'API
      if (!produit) {
        try {
          const response = await apiService.getProduit(produitId);
          produit = response?.produit || response;
          // Si le produit est trouvé, l'ajouter temporairement à la liste
          if (produit) {
            setProduits(prev => {
              const exists = prev.find(p => p.id === produitId);
              return exists ? prev : [...prev, produit];
            });
          }
        } catch (error) {
          console.error('Erreur lors du chargement du produit:', error);
          return;
        }
      }
      
      if (produit) {
        handleEditProduit(produit);
      } else {
        console.warn('[ProduitsManager] Produit non trouvé:', produitId);
      }
    }
  }), [produits]);

  // Filtrage et tri — produits + prestations catégorisées (catalogue unifié)
  const catalogueItems = [
    ...produits.map((p) => ({ ...p, _offerKind: 'produit' })),
    ...prestationsCatalogue.map((p) => ({
      ...p,
      _offerKind: 'prestation',
      typeProduit: 'SERVICE',
    })),
  ];

  let filteredProduits = catalogueItems.filter((produit) => {
    // Si la recherche est vide, on accepte tous les produits
    const matchesSearch = !searchTerm || searchTerm.trim() === '' ||
                         matchSearchText(produit.nom, searchTerm) ||
                         matchSearchText(produit.description, searchTerm) ||
                         matchSearchText(produit.categorie, searchTerm);
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'actif' && produit.actif) ||
                         (filterStatus === 'inactif' && !produit.actif);
    return matchesSearch && matchesFilter;
  });
  

  filteredProduits = filteredProduits.sort((a, b) => {
    if (sortBy === 'ordre') return (a.ordre || 0) - (b.ordre || 0);
    if (sortBy === 'nom') return (a.nom || '').localeCompare(b.nom || '');
    if (sortBy === 'prix') return (a.prix || 0) - (b.prix || 0);
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}
      {/* Header avec actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Catalogue ({catalogueItems.length})
          </h3>
          <p className="text-sm text-gray-500">
            Produits et prestations catégorisées
          </p>
        </div>
        {!embeddedAddMode && (
          <button
            onClick={() => {
              if (onAddProduit) {
                onAddProduit();
              } else {
                openCreateFlow();
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter une offre</span>
          </button>
        )}
      </div>

      {/* Filtres et recherche */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tous</option>
              <option value="actif">Actifs</option>
              <option value="inactif">Inactifs</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ordre">Tri par ordre</option>
              <option value="nom">Tri par nom</option>
              <option value="prix">Tri par prix</option>
            </select>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Message informatif sur les produits masqués */}
      {filterStatus === 'all' && produits.some(p => !p.actif) && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <Info className="h-4 w-4 inline mr-1" />
            <strong>Note :</strong> Les produits masqués (avec badge "Masqué du site") ne sont pas supprimés, ils sont juste invisibles sur le site web. Tu peux les remettre en ligne à tout moment avec le bouton de disponibilité.
          </p>
        </div>
      )}

      {/* Liste des produits */}
      {filteredProduits.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm && searchTerm.trim() !== ''
              ? `Aucun produit ne correspond à la recherche "${searchTerm}". ${filterStatus !== 'all' ? `Filtre actif: ${filterStatus === 'actif' ? 'Actifs' : 'Inactifs'}.` : ''} ${produits.length > 0 ? `(${produits.length} produit(s) au total dans la base)` : ''}`
              : filterStatus !== 'all'
              ? `Aucun produit ne correspond au filtre "${filterStatus === 'actif' ? 'Actifs' : 'Inactifs'}".`
              : 'Commencez par ajouter votre premier produit.'
            }
          </p>
          {searchTerm && searchTerm.trim() !== '' && produits.length > 0 && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
            >
              Effacer la recherche
            </button>
          )}
          {!searchTerm && filterStatus === 'all' && (
            <button
              onClick={openCreateFlow}
              className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une offre</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProduits.map((produit) => (
            <div 
              key={produit.id} 
              className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                !produit.actif 
                  ? 'border-red-300 bg-red-50/30' 
                  : produit.indisponible 
                    ? 'border-orange-300 bg-orange-50/30' 
                    : 'border-gray-200'
              }`}
              onClick={() => handleEditProduit(produit)}
            >
              {/* Image du produit */}
              <div className="relative">
                {produit.images && produit.images.length > 0 && produit.images[0] ? (
                  <img 
                    src={cloudinaryDeliveryUrl(produit.images[0], { width: CLOUDINARY_ADMIN_WIDTH.card }) || produit.images[0]} 
                    alt={produit.nom}
                    className="w-full h-48 object-cover rounded-t-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-full h-48 ${produit.images && produit.images.length > 0 && produit.images[0] ? 'hidden' : 'flex'} items-center justify-center bg-gray-100 rounded-t-lg`}
                >
                  <ImageIcon className="h-12 w-12 text-gray-400" />
                </div>
                
                {/* Badges */}
                <div className="absolute top-2 right-2 flex flex-col gap-1">
                  {produit.indisponible && produit.actif ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <Ban className="h-3 w-3 mr-1" />
                      Indisponible
                    </span>
                  ) : !produit.actif ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                      <EyeOff className="h-3 w-3 mr-1" />
                      Masqué du site
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Disponible
                    </span>
                  )}
                  {produit.favori && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <Star className="h-3 w-3 mr-1" />
                      Favori
                    </span>
                  )}
                  {produit.prixPromo && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Promo
                    </span>
                  )}
                  {(() => {
                    const badgeLabel = formatProduitOptionsBadgeLabel(
                      resolveProduitOptionsCount(produit)
                    );
                    if (!badgeLabel) return null;
                    return (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <Settings className="h-3 w-3 mr-1" />
                        {badgeLabel}
                      </span>
                    );
                  })()}
                </div>

                {/* Stock faible */}
                {produit.stock >= 0 && produit.stock < 5 && (
                  <div className="absolute bottom-2 left-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Stock faible
                    </span>
                  </div>
                )}
              </div>

              {/* Contenu du produit */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1 flex-wrap">
                      <h4 className="font-medium text-gray-900 text-lg">{produit.nom}</h4>
                      {(produit._offerKind === 'prestation' || produit.typeProduit === 'SERVICE') && (
                        <div className="flex items-center space-x-1">
                          <Scissors className="h-4 w-4 text-blue-600" />
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            Prestation
                          </span>
                        </div>
                      )}
                      {produit._offerKind === 'prestation' && produit.reservationEnLigne !== false && (
                        <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                          Réservable
                        </span>
                      )}
                    </div>
                    {(produit.categorie || produit.sousCategorie) && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Tag className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {produit.categorie}
                          {produit.sousCategorie ? ` › ${produit.sousCategorie}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (produit._offerKind === 'prestation') return;
                      handleToggleFavori(produit);
                    }}
                    className={`p-1 rounded ${
                      produit._offerKind === 'prestation'
                        ? 'invisible'
                        : produit.favori 
                        ? 'text-yellow-500 hover:text-yellow-600' 
                        : 'text-gray-300 hover:text-gray-400'
                    }`}
                    title={produit.favori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  >
                    <Star className={`h-5 w-5 ${produit.favori ? 'fill-current' : ''}`} />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {produit.description || 'Aucune description'}
                </p>

                {/* Infos produit */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Prix</span>
                    <div className="flex items-center space-x-2">
                      {produit.venteAuPoids && produit.prixAuPoids ? (
                        <div className="flex flex-col items-end">
                          <span className="text-lg font-bold text-primary">
                            {parseFloat(produit.prixAuPoids).toFixed(2)}€/{produit.unitePoids || 'kg'}
                          </span>
                          <span className="text-xs text-gray-500">Prix au poids</span>
                        </div>
                      ) : (
                        <>
                          {produit.prixPromo && (
                            <span className="text-sm text-gray-400 line-through">{parseFloat(produit.prix).toFixed(2)}€</span>
                          )}
                          <span className={`text-lg font-bold ${produit.prixPromo ? 'text-red-600' : 'text-green-600'}`}>
                            {parseFloat(produit.prixPromo || produit.prix || 0).toFixed(2)}€
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Durée pour les services / prestations */}
                  {(produit._offerKind === 'prestation' || produit.typeProduit === 'SERVICE') && produit.dureeMinutes && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Durée</span>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="font-medium text-gray-700">{produit.dureeMinutes} min</span>
                      </div>
                    </div>
                  )}

                  {produit._offerKind !== 'prestation' && produit.typeProduit !== 'SERVICE' && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Stock</span>
                      <span className={`font-medium ${
                        produit.stock === -1 ? 'text-blue-600' :
                        produit.stock < 5 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {produit.stock === -1 ? 'Illimité' : produit.stock}
                      </span>
                    </div>
                  )}

                  {produit._offerKind !== 'prestation' && produit.typeProduit !== 'SERVICE' && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Préparation</span>
                      <span className="text-gray-700">
                        {produit.tempsPreparationPersonnalise
                          ? `${Number(produit.tempsPreparation) || 0}min · spécifique`
                          : `${Number(
                              produit.tempsPreparationEffectif ??
                                commercantTempsPreparation ??
                                15
                            )}min`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Options alimentaires */}
                {(produit.vegetarien || produit.vegan || produit.sansGluten) && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {produit.vegetarien && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        <Leaf className="h-3 w-3 mr-1" />
                        Végétarien
                      </span>
                    )}
                    {produit.vegan && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        <Leaf className="h-3 w-3 mr-1" />
                        Vegan
                      </span>
                    )}
                    {produit.sansGluten && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        Sans gluten
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end space-x-1 pt-3 border-t border-gray-100">
                  {/* Menu disponibilité (produits uniquement) */}
                  {produit._offerKind !== 'prestation' ? (
                  <div className="relative" ref={(el) => {
                    if (!availabilityMenuRefs.current[produit.id]) {
                      availabilityMenuRefs.current[produit.id] = { current: el };
                    } else {
                      availabilityMenuRefs.current[produit.id].current = el;
                    }
                  }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation(); // Empêcher l'ouverture du modal
                        const newValue = availabilityMenuOpen === produit.id ? null : produit.id;
                        setAvailabilityMenuOpen(newValue);
                      }}
                      className={`p-1.5 rounded transition-colors ${
                        produit.indisponible && produit.actif
                          ? 'text-orange-600 hover:bg-orange-50' 
                          : !produit.actif
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={
                        produit.indisponible && produit.actif
                          ? 'Indisponible mais visible'
                          : !produit.actif
                          ? 'Masqué du site'
                          : 'Disponible'
                      }
                    >
                      {produit.indisponible && produit.actif ? (
                        <PowerOff className="h-4 w-4" />
                      ) : !produit.actif ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </button>
                    
                    {/* Menu déroulant */}
                    {availabilityMenuOpen === produit.id && (
                      <div 
                        className="absolute bg-white rounded-lg shadow-xl border-2 border-blue-500 py-2 z-50"
                        style={{ 
                          width: '256px',
                          top: '100%',
                          right: '0',
                          marginTop: '4px',
                          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {produit.actif && !produit.indisponible ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetIndisponibleVisible(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-2"
                            >
                              <Ban className="h-4 w-4 text-orange-600" />
                              <span>Indisponible (reste visible)</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetIndisponibleMasque(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2"
                            >
                              <PowerOff className="h-4 w-4 text-red-600" />
                              <span>Retirer du site</span>
                            </button>
                          </>
                        ) : produit.indisponible && produit.actif ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDisponible(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 flex items-center gap-2"
                            >
                              <Power className="h-4 w-4 text-green-600" />
                              <span>Rendre disponible</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetIndisponibleMasque(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2"
                            >
                              <PowerOff className="h-4 w-4 text-red-600" />
                              <span>Retirer du site</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDisponible(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 flex items-center gap-2"
                            >
                              <Power className="h-4 w-4 text-green-600" />
                              <span>Remettre en ligne</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetIndisponibleVisible(produit);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-2"
                            >
                              <Ban className="h-4 w-4 text-orange-600" />
                              <span>Indisponible (reste visible)</span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  ) : null}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture du modal
                      handleEditProduit(produit);
                    }}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {produit._offerKind !== 'prestation' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture du modal
                      handleDeleteProduit(produit.id);
                    }}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProduitAssistantWizard
        isOpen={showAssistant}
        onClose={() => setShowAssistant(false)}
        commercantId={commercantId}
        produits={produits}
        onComplete={handleAssistantComplete}
      />

      <OffreCreatePicker
        isOpen={showOffreCreatePicker}
        onClose={() => setShowOffreCreatePicker(false)}
        onConfirm={handleOffreCreateConfirm}
      />

      {/* Modal d'ajout/édition avec ProduitModal complet */}
      <ProduitModal 
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedProduit(null);
          if (!selectedProduit) {
            createArchetypeRef.current = null;
          }
        }}
        produit={selectedProduit}
        commercantId={commercantId}
        initialCategories={categories}
        initialTypeProduit={selectedProduit ? undefined : createTypeProduit}
        creationOptimized={!selectedProduit && createTypeProduit !== 'SERVICE'}
        archetypeId={null}
        archetypeAnswers={null}
        onRequestChangeArchetype={undefined}
        onSave={async (savedProduit) => {
          // Source unique = GET liste (stratégie B). Ne pas append savedProduit :
          // refetch + [...prev, savedProduit] dupliquait la carte jusqu’au refresh.
          const serverList = await fetchProduits();
          const nextProduits = produitsAfterModalSave({
            serverList,
            savedProduit,
            isCreate: !selectedProduit,
          });
          if (nextProduits) {
            setProduits(nextProduits);
          }
          setShowModal(false);
          setSelectedProduit(null);
          createArchetypeRef.current = null;

          // Post-conversion SERVICE → Prestation : ouvrir l’édition (jamais create)
          consumeConvertedPrestationFromSave(savedProduit, onRequestEditPrestation);
        }}
      />
    </div>
  );
});

export default ProduitsManager;
